import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { DOCUMENT, isPlatformServer } from '@angular/common';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';

import { TestVariant } from '@fcom/common';
import { ButtonComponent, ButtonSize, ButtonTheme, ChipTheme } from '@fcom/ui-components';
import { getKeyCodeFromEvent, isBlank, isNotBlank, isNotEmpty, KeyCodes, unsubscribe } from '@fcom/core/utils';
import { flattenDestinations } from '@fcom/destination-search/utils/flatten';
import {
  DestinationSearchItem,
  DestinationSearchItemFilterSelection,
  DestinationSearchItemType,
  FilterAction,
} from '@fcom/destination-search/interfaces';

@Component({
  selector: 'fin-destination-search-filter-to-field',
  templateUrl: 'destination-search-filter-to-field.component.html',
  styleUrls: ['destination-search-filter-to-field.component.scss'],
})
export class DestinationSearchFilterToFieldComponent implements OnInit, OnDestroy, AfterViewInit {
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly ChipTheme = ChipTheme;
  readonly TestVariant = TestVariant;

  @Input()
  selectedDestinations: DestinationSearchItem[] = [];

  /**
   * Destinations that the user can choose from, also contains countries and continents
   */
  @Input()
  allDestinations$: Observable<DestinationSearchItem[]>;

  /**
   * Emits this.selectedDestinationIds
   */
  @Output() valueChange = new EventEmitter<DestinationSearchItemFilterSelection>();

  @ViewChild('inputField')
  inputField: ElementRef;

  @ViewChild('inputFieldButton', { static: false })
  inputFieldButton: ElementRef;

  @ViewChild('optionsListbox', { static: false })
  optionsListbox: ElementRef;

  @ViewChild('showResultsButton', { static: false })
  showResultsButton: ButtonComponent;

  @ViewChild('tagList', { static: false })
  tagList: ElementRef;

  /**
   * Destinations that match the given query
   */
  matchingDestinations$: BehaviorSubject<DestinationSearchItem[]> = new BehaviorSubject(undefined);

  /**
   * Destination options list visibility
   */
  optionsListVisible$: BehaviorSubject<boolean> = new BehaviorSubject(false); // Options hidden by default

  /**
   * Highlighted result for keyboard navigation
   */
  highlightedItem$: BehaviorSubject<number> = new BehaviorSubject(-1);

  destinationInput = new UntypedFormControl();
  DESTINATION_TYPE = DestinationSearchItemType;

  private subscriptions: Subscription = new Subscription();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platform: Record<string, unknown>,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(
      combineLatest([
        this.destinationInput.valueChanges.pipe(debounceTime(200), distinctUntilChanged(), startWith('')),
        this.allDestinations$.pipe(filter(Boolean), distinctUntilChanged()),
      ])
        .pipe(
          map(([query, destinations]: [string, DestinationSearchItem[]]) => {
            const updated = this.mapQueryMatches(destinations, query);
            return flattenDestinations(updated).filter((d: DestinationSearchItem) => d.matchesQuery);
          })
        )
        .subscribe((destinations) => {
          this.updateDestinationOptionsList(destinations);
        })
    );
  }

  ngAfterViewInit(): void {
    this.subscriptions.add(
      this.highlightedItem$.subscribe((index) => {
        if (index >= 0) {
          const highlightedElement = this.optionsListbox?.nativeElement.querySelector(`#to-field-option-${index}`);
          if (!highlightedElement) {
            return;
          }

          if (!this.isScrolledIntoView(highlightedElement)) {
            // Because the list is still hidden when this is evaluated the offsetTop will be 0 for all items
            // when opening the list. With timeout we'll skip one event cycle and it'll be calculated correctly
            setTimeout(() => {
              this.optionsListbox.nativeElement.scrollTop = highlightedElement.offsetTop;
            }, 0);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions = unsubscribe(this.subscriptions);
  }

  get inputHasFocus(): boolean {
    return this.document.activeElement === this.inputField?.nativeElement;
  }

  handleComponentFocusOut(event: FocusEvent): void {
    const el: HTMLElement = this.elementRef?.nativeElement;

    if (!!el && !el.contains(event.relatedTarget as HTMLElement)) {
      event.stopPropagation();
      this.closeOptionsList(false);
    }
  }

  onClickInputArea(event: MouseEvent): void {
    // Distinguish between button area and input field clicks
    if (this.inputFieldButton?.nativeElement.contains(event.target)) {
      // eslint-disable-next-line rxjs/no-subject-value
      if (!this.optionsListVisible$.getValue()) {
        this.openOptionsList();
      } else {
        this.closeOptionsList(false);
      }
    } else {
      this.openOptionsList();
    }
  }

  openOptionsList(fromBottom = false): void {
    this.optionsListVisible$.next(true);
    this.highlightedItem$.next(fromBottom ? this.maxHighlightIndex : 0);
  }

  closeOptionsList(handleFocus = true): void {
    if (handleFocus) {
      this.inputFieldButton?.nativeElement?.focus();
    }

    this.highlightedItem$.next(-1);
    this.destinationInput.reset();
    this.optionsListVisible$.next(false);
  }

  removeTag(destination: DestinationSearchItem): void {
    const showButton: HTMLElement = this.showResultsButton?.buttonElement?.nativeElement;
    const tags: NodeListOf<HTMLElement> = this.tagList?.nativeElement.querySelectorAll('fcom-chip');
    const remainingTags = Array.from(tags).filter((c: HTMLElement) => c.id !== destination.id);
    const lastTag = remainingTags[remainingTags?.length - 1];

    if (lastTag) {
      lastTag.querySelector('button').focus();
    } else {
      showButton.focus();
    }
    this.valueChange.emit({
      action: FilterAction.REMOVE,
      destination,
      selectedDestinations: this.selectedDestinations,
    });
  }

  /**
   * Keyboard navigation according to https://www.w3.org/TR/wai-aria-practices/examples/combobox/aria1.1pattern/listbox-combo.html
   */
  onDestinationInputKeydown(event: KeyboardEvent): void {
    // eslint-disable-next-line rxjs/no-subject-value
    const optionsListOpen = this.optionsListVisible$.getValue();

    switch (getKeyCodeFromEvent(event)) {
      case KeyCodes.ESC:
        this.closeOptionsList();
        break;
      case KeyCodes.UP:
        if (!optionsListOpen) {
          this.openOptionsList(true);
        } else {
          this.highlightPreviousItem();
        }
        break;
      case KeyCodes.DOWN:
        if (!optionsListOpen) {
          this.openOptionsList();
        } else {
          this.highlightNextItem();
        }
        break;
      case KeyCodes.ENTER:
        if (this.highlightIndex > -1) {
          // eslint-disable-next-line rxjs/no-subject-value
          const highlightedElement = this.matchingDestinations$.getValue()[this.highlightIndex];
          this.toggleDestinationSelection(highlightedElement);
        } else {
          this.closeOptionsList();
        }
        break;
      default:
        break;
    }
    /* eslint-enable rxjs/no-subject-value */
  }

  toggleDestinationSelection(destination: DestinationSearchItem): void {
    const isSelected = Boolean(this.selectedDestinations?.find((selected) => selected.id === destination.id));
    this.valueChange.emit({
      action: isSelected ? FilterAction.REMOVE : FilterAction.ADD,
      destination,
      selectedDestinations: this.selectedDestinations,
    });
  }

  destinationIsSelected(destination: DestinationSearchItem): boolean {
    // eslint-disable-next-line rxjs/no-subject-value
    return Boolean(this.selectedDestinations?.find((d) => d.id === destination.id));
  }

  destinationIsHighlighted(destinationIndex: number): boolean {
    return this.inputHasFocus && this.highlightIndex === destinationIndex;
  }

  private get highlightIndex(): number {
    // eslint-disable-next-line rxjs/no-subject-value
    return this.highlightedItem$.getValue();
  }

  private get maxHighlightIndex(): number {
    // eslint-disable-next-line rxjs/no-subject-value
    return this.matchingDestinations$.getValue().length - 1;
  }

  private highlightNextItem(): void {
    const nextIndex = this.highlightIndex < this.maxHighlightIndex ? this.highlightIndex + 1 : 0;
    this.highlightedItem$.next(nextIndex);
  }

  private highlightPreviousItem(): void {
    const nextIndex = this.highlightIndex > 0 ? this.highlightIndex - 1 : this.maxHighlightIndex;
    this.highlightedItem$.next(nextIndex);
  }

  private isScrolledIntoView(element: HTMLElement): boolean {
    if (isPlatformServer(this.platform)) {
      return true;
    }
    const item = element?.getBoundingClientRect(),
      top = item.top,
      height = item.height,
      itemParent = element.parentNode as HTMLElement,
      itemParentRect = itemParent?.getBoundingClientRect();

    if (item.bottom < 0) {
      return false;
    }
    if (top > document.documentElement.clientHeight) {
      return false;
    }
    if (top <= itemParentRect.bottom === false) {
      return false;
    }
    if (top + height <= itemParentRect.top) {
      return false;
    }
    return true;
  }

  /**
   * test if destination's attributes contain words that start with the given query
   */
  private destinationMatchesQuery(destination: DestinationSearchItem, query: string): boolean {
    // title that we test if it contains any of the queries.
    // Mark each word start with a space so that search “sin” doesn’t match “Helsinki”,
    // but we can match on every word start, including “Palma De Mallorca’s” Mallorca
    const t =
      ` ${destination.title} ${destination.normalizedTitle} ${destination.titleMaster} ${destination.normalizedTitleMaster} ${destination.country} ${destination.countryMaster} ${destination.continentMaster} ${destination.continent}`.toLowerCase();

    return t.includes(` ${query?.toLowerCase()}`);
  }

  private recursiveMatchCheck(destination: DestinationSearchItem, query: string): boolean {
    const selfMatch = this.destinationMatchesQuery(destination, query);

    if (isNotEmpty(destination.items)) {
      const childMatch = destination.items.some((child) => this.recursiveMatchCheck(child, query));
      return selfMatch || childMatch;
    }

    return selfMatch;
  }

  private mapQueryMatches(destinations: DestinationSearchItem[], query: string): DestinationSearchItem[] {
    return destinations?.map((destination) => ({
      ...destination,
      items: this.mapQueryMatches(destination.items, query),
      matchesQuery: isBlank(query) || this.recursiveMatchCheck(destination, query),
    }));
  }

  private updateDestinationOptionsList(destinations: DestinationSearchItem[]): void {
    const query = this.destinationInput.value;
    const selectedItem = destinations.findIndex((d) => this.destinationMatchesQuery(d, query));

    this.matchingDestinations$.next(destinations);
    this.highlightedItem$.next(selectedItem);

    // eslint-disable-next-line rxjs/no-subject-value
    if (isNotBlank(query) && !this.optionsListVisible$.getValue()) {
      this.openOptionsList();
    }
  }
}
